import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

export default function AddVideoModal({ open, onClose, onSuccess, config }) {
  const [uploading, setUploading] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      videoUrl: '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    if (!uploading) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setUploading(true);
    try {
      await api.admin.ingestVideo({
        title: data.title,
        url: data.videoUrl,
      });
      toast.success(config.successMessage);
      handleClose();
      onSuccess();
    } catch (error) {
      console.error(`Failed to add ${config.providerName} video:`, error);
      toast.error(error.message || config.errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>{config.modalTitle}</span>
          <IconButton onClick={handleClose} disabled={uploading} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="space-y-4">
            <Controller
              name="title"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title *"
                  placeholder="Enter video title"
                  fullWidth
                  disabled={uploading}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />

            <Controller
              name="videoUrl"
              control={control}
              rules={config.urlValidation}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={config.urlLabel}
                  placeholder={config.urlPlaceholder}
                  fullWidth
                  disabled={uploading}
                  error={!!errors.videoUrl}
                  helperText={errors.videoUrl?.message}
                />
              )}
            />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={uploading}
          >
            {uploading ? 'Adding...' : 'Add Video'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
